import React, { useEffect, useRef, useState } from 'react';
import './LivingSite.css';
import { createSim } from './livingSiteSim';
import {
  LIVING_SITE_IMAGE, IMAGE_W, IMAGE_H, TOWERS as DEFAULT_TOWERS, STREETS as DEFAULT_STREETS,
  WALK_ZONES as DEFAULT_WALK_ZONES, TREE_ROWS as DEFAULT_TREE_ROWS, TREE_AREAS as DEFAULT_TREE_AREAS,
  LAMPS as DEFAULT_LAMPS, TRAMS as DEFAULT_TRAMS, SPARKLES as DEFAULT_SPARKLES, CLOUDS as DEFAULT_CLOUDS,
} from '../data/livingSiteData';

const px = n => n + 'px';

function rng(seed) {
  let a = seed;
  return () => {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function el(parent, cls, styles) {
  const d = document.createElement('div');
  d.className = cls;
  d.setAttribute('aria-hidden', 'true');
  Object.keys(styles || {}).forEach(k => d.style.setProperty(k, styles[k]));
  parent.appendChild(d);
  return d;
}

// the lane of a street: the centre-line moved sideways by d pixels
function shift(pts, d) {
  return pts.map((p, i) => {
    const a = pts[Math.max(0, i - 1)], b = pts[Math.min(pts.length - 1, i + 1)];
    const dx = b[0] - a[0], dy = b[1] - a[1], l = Math.hypot(dx, dy) || 1;
    return [p[0] - dy / l * d, p[1] + dx / l * d];
  });
}
const pathLength = pts => pts.slice(1).reduce((t, p, i) => t + Math.hypot(p[0] - pts[i][0], p[1] - pts[i][1]), 0);

/**
 * "המתחם בתנועה": an aerial photo of the complex with cars, trams, people, trees, water and light over it.
 * Props (all optional): image, towers, streets, walkZones, treeRows, treeAreas, lamps, trams, sparkles, clouds (the scene's data),
 * units (building number -> the small label under the number), found (array of building numbers lit in blue), selected (building number),
 * onSelect(buildingNumber) (fired on a click on a building; the same building again gives 0), title, showBar.
 */
export default function LivingSite({
  image = LIVING_SITE_IMAGE, towers = DEFAULT_TOWERS, streets = DEFAULT_STREETS, walkZones = DEFAULT_WALK_ZONES,
  treeRows = DEFAULT_TREE_ROWS, treeAreas = DEFAULT_TREE_AREAS, lamps = DEFAULT_LAMPS, trams = DEFAULT_TRAMS,
  sparkles = DEFAULT_SPARKLES, clouds = DEFAULT_CLOUDS,
  units, found, selected, onSelect, title = 'המתחם בתנועה', showBar = true, style,
}) {
  const rootRef = useRef(null);
  const stageRef = useRef(null);
  const sceneRef = useRef(null);
  const towerEls = useRef({});
  const nightRef = useRef(0);
  const stillRef = useRef(false);
  const onSelectRef = useRef(onSelect);
  onSelectRef.current = onSelect;
  const [mode, setMode] = useState('day');
  const [paused, setPaused] = useState(false);
  const [innerSelected, setInnerSelected] = useState(0);
  // the device asks for less motion: the scene starts still, and a button lets the viewer turn the motion on
  const [reduced, setReduced] = useState(() => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const [forced, setForced] = useState(false);
  const still = reduced && !forced;
  const sel = selected !== undefined ? selected : innerSelected;
  nightRef.current = mode === 'night' ? 1 : mode === 'dusk' ? 0.5 : 0;
  stillRef.current = paused || still;

  // build the scene once (and again if the data changes)
  useEffect(() => {
    const scene = sceneRef.current, stage = stageRef.current;
    if (!scene || !stage) return undefined;
    scene.innerHTML = '';
    towerEls.current = {};
    const r = rng(7);

    const img = document.createElement('img');
    img.src = image; img.alt = 'תצלום אווירי של המתחם'; img.draggable = false; img.width = IMAGE_W; img.height = IMAGE_H;
    scene.appendChild(img);

    const blob = (x, y, size) => {
      const dur = 3 + r() * 3.5;
      el(scene, 'ls-item ls-tree', { left: px(x - size / 2), top: px(y - size / 2), width: px(size), height: px(size), '--dur': dur + 's', '--delay': -r() * dur + 's' });
    };
    treeAreas.forEach(a => { for (let i = 0; i < a[4]; i++) blob(a[0] + r() * a[1], a[2] + r() * a[3], a[5] + r() * a[6]); });
    treeRows.forEach(row => { for (let x = row[0]; x <= row[1]; x += row[3]) blob(x, row[2], row[4]); });

    clouds.forEach(c => el(scene, 'ls-item ls-cloud', { top: px(c[0]), width: px(c[1]), height: px(c[2]), '--dur': c[3] + 's', '--delay': -c[3] * 0.4 + 's' }));
    el(scene, 'ls-item ls-sweep');

    [['ls-tint dusk'], ['ls-tint night']].forEach(([cls]) => el(scene, cls, { width: px(IMAGE_W), height: px(IMAGE_H) }));
    lamps.forEach(p => el(scene, 'ls-item ls-lamp', { left: px(p[0] - 22), top: px(p[1] - 22) }));

    // cars, pedestrians and the light rail: one canvas on top of the photo (see livingSiteSim.js)
    const cvs = document.createElement('canvas');
    const SIM_K = 1.5;
    cvs.width = Math.round(IMAGE_W * SIM_K); cvs.height = Math.round(IMAGE_H * SIM_K);
    cvs.style.cssText = 'left:0;top:0;width:' + IMAGE_W + 'px;height:' + IMAGE_H + 'px;pointer-events:none';
    cvs.setAttribute('aria-hidden', 'true');
    scene.appendChild(cvs);
    const sctx = cvs.getContext('2d');
    const sim = createSim(sctx, () => nightRef.current);
    let raf = 0, last = 0;
    const frame = ts => {
      const dt = Math.min(0.05, (ts - last) / 1000 || 0.016); last = ts;
      if (!stillRef.current) sim.step(dt);
      sctx.setTransform(SIM_K, 0, 0, SIM_K, 0, 0); sctx.clearRect(0, 0, IMAGE_W, IMAGE_H);
      sim.draw();
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    sparkles.forEach(s => {
      for (let i = 0; i < s[0]; i++) {
        const dur = 1.6 + r() * 1.8;
        el(scene, 'ls-item ls-sparkle', { left: px(s[1] + r() * s[2]), top: px(s[3] + r() * s[4]), '--dur': dur + 's', '--delay': -r() * dur + 's' });
      }
    });

    towers.forEach((t, i) => {
      const wrap = el(scene, 'ls-tower', { left: px(t.x), top: px(t.y), width: px(t.w), height: px(t.h), '--dur': 3 + (i % 3) * 0.7 + 's', '--delay': -i * 0.5 + 's' });
      wrap.removeAttribute('aria-hidden');
      wrap.innerHTML = '<div class="glow"></div><div class="ring"></div><div class="ls-label"><b></b><span></span></div>';
      wrap.querySelector('b').textContent = t.n;
      const btn = document.createElement('button');
      btn.type = 'button'; btn.setAttribute('aria-pressed', 'false');
      btn.addEventListener('click', () => {
        if (onSelectRef.current) onSelectRef.current(t.n);
        else setInnerSelected(prev => (prev === t.n ? 0 : t.n));
      });
      wrap.appendChild(btn);
      towerEls.current[t.n] = { wrap, btn, label: wrap.querySelector('.ls-label span') };
    });

    const fit = () => scene.style.setProperty('--ls-scale', stage.clientWidth / IMAGE_W);
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(stage);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); scene.innerHTML = ''; towerEls.current = {}; };
  }, [image, towers, streets, walkZones, treeRows, treeAreas, lamps, trams, sparkles, clouds]);

  // the state of the buildings: selected, found, the units label
  useEffect(() => {
    const foundSet = new Set(found || []);
    towers.forEach(t => {
      const o = towerEls.current[t.n]; if (!o) return;
      const on = sel === t.n, lab = (units && units[t.n]) || t.u;
      o.wrap.classList.toggle('selected', on);
      o.wrap.classList.toggle('found', foundSet.has(t.n));
      o.btn.setAttribute('aria-pressed', String(on));
      o.btn.setAttribute('aria-label', 'בניין ' + t.n + (lab ? ', ' + lab : ''));
      o.label.textContent = lab || '';
      o.label.style.display = lab ? '' : 'none';
    });
  }, [sel, found, units, towers, image]);

  const selTower = towers.find(t => t.n === sel);
  const selLabel = selTower ? ((units && units[selTower.n]) || selTower.u) : '';

  return (
    <div ref={rootRef} className={'living-site' + (paused ? ' ls-paused' : '') + (still ? ' ls-still' : '')} data-mode={mode} style={style}>
      <div ref={stageRef} className="ls-stage" style={{ aspectRatio: IMAGE_W + ' / ' + IMAGE_H }}>
        <div className="ls-camera"><div ref={sceneRef} className="ls-scene" style={{ width: IMAGE_W, height: IMAGE_H }} /></div>
        <div className="ls-vignette" />
      </div>
      {showBar && (
        <div className="ls-bar">
          <div>
            <span className="ls-title">{title}</span>
            <span className="ls-info" aria-live="polite">{selTower ? 'בניין ' + selTower.n + (selLabel ? ' · ' + selLabel : '') : 'לחצו על בניין כדי לסמן אותו'}</span>
          </div>
          <div className="ls-controls">
            {[['day', 'יום'], ['dusk', 'שקיעה'], ['night', 'לילה']].map(([m, label]) => (
              <button key={m} type="button" aria-pressed={mode === m} onClick={() => setMode(m)}>{label}</button>
            ))}
            {still
              ? <button type="button" aria-pressed="false" onClick={() => setForced(true)}>הפעל תנועה</button>
              : <button type="button" aria-pressed={paused} onClick={() => setPaused(p => !p)}>{paused ? 'הפעל תנועה' : 'עצור תנועה'}</button>}
          </div>
        </div>
      )}
    </div>
  );
}
